import React from 'react';
import { Button, Tag } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { usePermissions } from '../../../contexts/PermissionContext';
import MobileTable from '../../../Component/MobileTable';

interface ReceiptsTableProps {
  receipts: any[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

const ReceiptsTable: React.FC<ReceiptsTableProps> = ({
  receipts,
  loading,
  currentPage,
  pageSize,
  total,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const history = useHistory();

  const handleViewReceipt = (receiptId: number) => {
    history.push(`/receipts/${receiptId}`);
  };

  const receiptColumns = [
    {
      title: t('receipts.columns.reference', 'Reference'),
      dataIndex: 'reference',
      key: 'reference',
    },
    {
      title: t('receipts.columns.issueDate', 'Issue Date'),
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: t('receipts.columns.value', 'Value'),
      dataIndex: 'amount',
      key: 'amount',
      render: (value: any) => {
        const numValue = parseFloat(value);
        return isNaN(numValue) ? '0.00 ر.س' : `${numValue.toFixed(2)} ر.س`;
      },
    },
    {
      title: t('receipts.columns.status', 'Status'),
      dataIndex: 'kind',
      key: 'kind',
      render: (status: string) => <Tag color={status === 'قبض' ? 'green' : 'orange'}>{status}</Tag>,
    },
    {
      title: t('receipts.columns.actions', 'Actions'),
      key: 'actions',
      width: 80,
      render: (_: any, record: any) => {
        const canViewReceipt =
          hasPermission('view_receipt') ||
          hasPermission('receipts.read') ||
          hasPermission('receipts.view') ||
          hasPermission('customers.view_receipts');

        if (!canViewReceipt) {
          return null;
        }

        return (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewReceipt(record.id)}
          >
            {t('common.view', 'View')}
          </Button>
        );
      },
    },
  ];

  return (
    <MobileTable
      columns={receiptColumns}
      dataSource={receipts}
      rowKey="id"
      loading={loading}
      onRowClick={(record: any) => handleViewReceipt(record.id)}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        total: total,
        onChange: onPageChange,
        showSizeChanger: false,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      }}
      scroll={{ x: 800 }}
    />
  );
};

export default ReceiptsTable;
